import React, { useEffect, useMemo, useCallback } from 'react';
import { View, Text, Pressable } from 'react-native';
import { ActivityIndicator } from 'react-native-paper';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateNotifications, deleteNotifications } from "@/data/notifications";
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { LegendList } from "@legendapp/list";
import Toast from 'react-native-toast-message';
import { RefreshControl } from 'react-native-gesture-handler';
import { useNavigation, useRouter } from 'expo-router';
import { StyleSheet } from 'react-native-unistyles';
import DropdownMenu, { dropdownIcons } from '@/components/General/DropdownMenu';
import Unicon from '@/components/General/Unicon';
import CutCard from '@/components/General/CutCard';
import FeatureIcon, { getFeatureColorKey } from '@/components/General/FeatureIcon';
import { Tooltip } from '@/components/General/Tooltip';

// NotificationType.module slug → FeatureIcon feature key. FeatureIcon owns
// both the glyph and the module color (getFeatureColorKey → themes.js key),
// so the card accent and the icon always match.
const NOTIFICATION_MODULE_MAP = {
    tasks: 'tasks',
    giving: 'giving',
    contributions: 'giving',
    connect: 'connect',
    boards: 'connect',
    messages: 'connect',
    forms: 'forms',
    smallgroups: 'groups',
    registrations: 'people',
    admin: 'roles',
    people: 'people',
    events: 'events',
    training: 'training',
    paths: 'paths',
    automations: 'paths', // canonical module name → same icon
    follow_up: 'paths',    // future notification module → automations icon
    analytics: 'analytics',
    files: 'files',
};
const DEFAULT_MODULE_FEATURE = 'dashboard';


const getTimeLabel = (dateStr) => {
    const now = new Date();
    const date = new Date(dateStr);
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date >= today) return 'Today';
    if (date >= yesterday) return 'Yesterday';
    return 'Earlier';
};

const formatTimestamp = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};


export default function NotificationsScreen({ onSelect }) {
    const navigation = useNavigation();
    const router = useRouter();
    const queryClient = useQueryClient();
    const { data: loggedInUser, isFetching, isLoading } = Query.people.loggedInUser();

    const notifications = useMemo(() => {
        return loggedInUser?.Notifications || [];
    }, [loggedInUser?.Notifications]);

    const unreadCount = useMemo(() => {
        return notifications.filter(n => !n.isRead).length;
    }, [notifications]);

    const groupedData = useMemo(() => {
        if (!notifications.length) return [];
        const groups = {};
        const order = ['Today', 'Yesterday', 'Earlier'];

        notifications.forEach(n => {
            const label = getTimeLabel(n.sentAt || n.updatedAt);
            if (!groups[label]) groups[label] = [];
            groups[label].push(n);
        });

        const result = [];
        order.forEach(label => {
            if (groups[label]?.length) {
                result.push({ type: 'header', label, id: `header-${label}` });
                groups[label].forEach(n => result.push({ type: 'notification', ...n }));
            }
        });
        return result;
    }, [notifications]);

    const markAsRead = useMutation({
        mutationFn: updateNotifications,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.people.loggedInUser() });
        },
    });

    const dismissNotification = useMutation({
        mutationFn: deleteNotifications,
        onMutate: (data) => {
            queryClient.setQueryData(QueryKeys.people.loggedInUser(), old => {
                if (!old) return old;
                const newNotifications = old.Notifications?.filter(n => !data?.ids?.includes(n.id));
                return { ...old, Notifications: newNotifications };
            });
        },
    });

    const menuOptions = useMemo(() => [
        ...(unreadCount > 0 ? [{
            name: "Mark all as read",
            icon: { android: 'done_all', ios: 'checkmark.circle', web: 'checkmark-done-outline' },
            onSelect: () => {
                if (onSelect) onSelect();
                markAsRead.mutateAsync(notifications.filter(n => !n.isRead).map(n => ({ id: n.id, isRead: true }))).then(() => {
                    Toast.delayShow({ type: 'success', text2: 'All notifications marked as read' });
                });
            }
        }] : []),
        ...(notifications.length > 0 ? [{
            name: "Dismiss all",
            destructive: true,
            icon: dropdownIcons.delete,
            onSelect: () => {
                if (onSelect) onSelect();
                dismissNotification.mutateAsync({ ids: notifications.map(n => n.id), targetPersonID: loggedInUser?.id }).then(() => {
                    queryClient.invalidateQueries({ queryKey: QueryKeys.people.loggedInUser() });
                    Toast.delayShow({ type: 'success', text2: 'All notifications dismissed' });
                });
            }
        }] : []),
        {
            name: "Notification Settings",
            icon: dropdownIcons.settings,
            onSelect: () => {
                if (onSelect) onSelect();
                router.push('/settings');
            }
        }
    ], [unreadCount, notifications, loggedInUser?.id, onSelect, router]);

    const headerActions = useMemo(() => (
        <DropdownMenu testID="notifications-actions" options={menuOptions} align="end" />
    ), [menuOptions]);

    // Embedded mode (onSelect set, e.g. web SheetModal drawer): no stack header
    // exists, so setOptions is a no-op — actions render inline above the list.
    useEffect(() => {
        if (onSelect || !navigation.isFocused()) return;
        navigation.setOptions({
            headerRight: () => headerActions,
        });
    }, [navigation, loggedInUser, onSelect, headerActions]);

    const handleNotificationPress = useCallback((item) => {
        if (item.path) {
            // Tap = mark read (not dismiss). Optimistic cache update so badges clear instantly.
            if (!item.isRead) {
                queryClient.setQueryData(QueryKeys.people.loggedInUser(), old => old ? ({
                    ...old,
                    Notifications: (old.Notifications || []).map(n => n.id === item.id ? { ...n, isRead: true } : n),
                }) : old);
                markAsRead.mutate([{ id: item.id, isRead: true }]);
            }
            // String form so query strings (e.g. people/{id}?tab=tasks) parse —
            // object-form pathname does not. Matches useNotificationObserver.
            router.push(item.path.startsWith('/') ? item.path : `/${item.path}`);
        }
    }, [loggedInUser?.id]);

    const handleDismiss = useCallback((item) => {
        dismissNotification.mutate({ ids: [item.id], targetPersonID: loggedInUser?.id });
    }, [loggedInUser?.id]);

    const renderItem = useCallback(({ item }) => {
        if (item.type === 'header') {
            return (
                <View style={styles.sectionHeader}>
                    <Text style={styles.sectionHeaderText}>{item.label}</Text>
                </View>
            );
        }

        const timestamp = item.sentAt || item.updatedAt;
        const moduleSlug = item.NotificationType?.module;
        const feature = NOTIFICATION_MODULE_MAP[moduleSlug] || DEFAULT_MODULE_FEATURE;

        // Tooltip owns the Pressable (tap = navigate, hover/long-press = full
        // message tooltip). CutCard stays non-interactive so its corner accents
        // render inside the shell that carries bg + hover/press feedback.
        return (
            <Tooltip
                testID={`Notification-${item.id}`}
                title={item.message ? item.message : undefined}
                onPress={() => handleNotificationPress(item)}
                style={({ pressed }) => [styles.notificationCardShell(item.isRead), pressed && styles.notificationCardPressed]}
            >
                <CutCard
                    accentColor={getFeatureColorKey(feature)}
                    style={styles.notificationCard}
                    contentStyle={styles.notificationCardContent}
                >
                    <FeatureIcon feature={feature} size={40} iconSize={20} />

                    <View style={styles.notificationContent}>
                        <View style={styles.notificationTitleRow}>
                            <Text numberOfLines={1} style={styles.notificationTitle(item.isRead)}>{item.title}</Text>
                            {!item.isRead && <View style={styles.unreadDot} />}
                        </View>
                        {!!item.message && <Text numberOfLines={4} style={styles.notificationMessage}>{item.message}</Text>}
                        {!!timestamp && <Text style={styles.notificationTime}>{formatTimestamp(timestamp)}</Text>}
                    </View>

                    <Pressable
                        testID={`Notification-dismiss-${item.id}`}
                        style={styles.dismissButton}
                        onPress={() => handleDismiss(item)}
                        hitSlop={12}
                    >
                        <Unicon name="close-outline" size={18} colorVariant="textTertiary" />
                    </Pressable>
                </CutCard>
            </Tooltip>
        );
    }, [handleNotificationPress, handleDismiss]);

    return (
        <View style={styles.container}>
            <View style={styles.listContainer}>
                {onSelect && (
                    <View style={styles.embeddedActions}>
                        {headerActions}
                    </View>
                )}
                <LegendList
                    recycleItems
                    keyboardShouldPersistTaps="handled"
                    keyExtractor={(item) => item.id}
                    estimatedItemSize={88}
                    data={!isLoading ? groupedData : []}
                    contentContainerStyle={styles.listContent}
                    renderItem={renderItem}
                    refreshControl={
                        <RefreshControl
                            refreshing={isFetching}
                            onRefresh={() => queryClient.invalidateQueries({ queryKey: QueryKeys.people.loggedInUser() })}
                        />
                    }
                    showsVerticalScrollIndicator={false}
                    ListFooterComponent={
                        isLoading ? (
                            <View style={styles.loaderContainer}>
                                <ActivityIndicator size="small" />
                            </View>
                        ) : notifications.length === 0 ? (
                            <View style={styles.emptyContainer}>
                                <EmptyListPlaceholder type="complete" icon="notifications-off-outline" spacing={10} message="You're all caught up!" />
                            </View>
                        ) : (
                            <View style={styles.footer} />
                        )
                    }
/>
            </View>
        </View>
    );
}


const styles = StyleSheet.create(({ colors, fonts, spacing, typography, shadows }) => ({
    container: {
        // flex: 1,
        alignItems: "center",
        backgroundColor: 'transparent',
    },
    listContainer: {
        // flex: 1,
        maxWidth: 700,
        width: "100%",
    },
    listContent: {
        paddingHorizontal: spacing.md,
        paddingTop: spacing.sm,
    },
    embeddedActions: {
        flexDirection: "row",
        justifyContent: "flex-end",
        paddingHorizontal: spacing.md,
        paddingTop: spacing.sm,
    },
    sectionHeader: {
        paddingTop: spacing.md,
        paddingBottom: spacing.sm,
        paddingHorizontal: 4,
    },
    sectionHeaderText: {
        fontSize: 13,
        fontFamily: fonts.bold,
        color: colors.textSecondary,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
    },
    // Tooltip's Pressable: carries the card surface + interactive feedback.
    // Bg lives here (not on CutCard) so the web hover tint is actually visible —
    // the CutCard inside is transparent. Mirrors CutCard's interactiveCard.
    notificationCardShell: (isRead) => ({
        backgroundColor: isRead ? colors.surface : colors.surfaceElevated,
        borderRadius: 3,
        marginBottom: spacing.sm,
        boxShadow: shadows.card,
        _web: {
            cursor: "pointer",
            transition: "background-color 0.16s ease, transform 0.16s ease, opacity 0.16s ease",
            _hover: {
                backgroundColor: colors.hoverCard,
                transform: [{ translateY: -1 }],
            },
            _active: {
                transform: [{ translateY: 0 }],
            },
        },
    }),
    notificationCard: {
        backgroundColor: 'transparent',
    },
    notificationCardContent: {
        flexDirection: "row",
        alignItems: "flex-start",
        padding: 14,
        gap: 12,
    },
    notificationCardPressed: {
        opacity: 0.85,
    },

    notificationContent: {
        flex: 1,
        gap: 3,
    },
    notificationTitleRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
    },
    notificationTitle: (isRead) => ({
        fontSize: typography.body,
        fontFamily: isRead ? fonts.body : fonts.bold,
        color: colors.text,
        flex: 1,
    }),
    unreadDot: {
        width: 8,
        height: 8,
        borderRadius: 4,
        backgroundColor: colors.primary,
        flexShrink: 0,
    },
    notificationMessage: {
        fontSize: typography.caption,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        lineHeight: 18,
    },
    notificationTime: {
        fontSize: typography.micro,
        fontFamily: fonts.body,
        color: colors.textTertiary,
        marginTop: 2,
    },
    dismissButton: {
        width: 32,
        height: 32,
        borderRadius: 8,
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        marginTop: -2,
        marginRight: -4,
        _web: {
            transition: "background-color 0.15s ease",
            _hover: {
                backgroundColor: colors.hoverStrong,
            },
        },
    },
    loaderContainer: {
        paddingVertical: spacing.xxl,
        alignItems: "center",
    },
    emptyContainer: {
        paddingBottom: spacing.xl,
    },
    footer: {
        paddingBottom: spacing.xl,
    },
}))